import { useCallback, useEffect, useRef, useState } from 'react';
import apiService from '../../services/ApiService';
import companionService from '../../services/CompanionService';
import { useDesktopAgent } from '../../contexts/DesktopAgentContext';
import type { ToolContentVersion } from './contentModel';

/** Una richiesta al Desktop Agent: improve, missing, rewrite, translate, block. */
export interface AgentRequest {
    kind: string;
    language: string;
    mode: 'improve' | 'missing' | 'rewrite' | 'translate' | 'block';
    instructions?: string;
    blockIndex?: number;
    blocks?: string;
}

export interface AgentResult { draft?: ToolContentVersion; block?: string }

/** prepare: l'API compone il prompt; running: lavora il Desktop Agent; checking: l'API valida e salva. */
export type AgentPhase = 'idle' | 'prepare' | 'running' | 'checking';

interface PreparedRun { prompt: string; healthPort: number; needsRepository: boolean }

const errorText = (error: unknown) =>
    (error as { response?: { data?: { error?: string } } })?.response?.data?.error
    ?? (error instanceof Error ? error.message : 'Operazione non riuscita');

/**
 * Esegue una richiesta di contenuto con il Desktop Agent: prepare sull'API → /run sul PC in
 * sola lettura → complete sull'API. Una sola alla volta; si annulla chiudendo la connessione.
 */
export function useAgentRun(toolId: number) {
    const agent = useDesktopAgent();
    const [phase, setPhase] = useState<AgentPhase>('idle');
    const [startedAt, setStartedAt] = useState<number | null>(null);
    const [lastLine, setLastLine] = useState('');
    const abort = useRef<AbortController | null>(null);

    useEffect(() => () => abort.current?.abort(), []);

    const run = useCallback(async (request: AgentRequest): Promise<AgentResult> => {
        if (agent.state !== 'ready' || !agent.agent) throw new Error('Il Desktop Agent non è pronto: controlla l’icona in alto a destra.');
        const controller = new AbortController();
        abort.current = controller;
        setStartedAt(Date.now());
        setLastLine('');
        try {
            setPhase('prepare');
            const prepared = await apiService.adminPrepareToolContentGeneration<PreparedRun>(toolId, request.kind, request.language, {
                mode: request.mode, instructions: request.instructions, blockIndex: request.blockIndex, blocks: request.blocks,
            }).catch(error => { throw new Error(errorText(error)); });
            if (prepared.needsRepository && !agent.workspace.trim())
                throw new Error('Indica la cartella del codice di Semplora: il Desktop Agent deve leggerlo.');
            if (controller.signal.aborted) throw new Error('Annullato');

            setPhase('running');
            const output = await companionService.runReadOnly(
                prepared.healthPort || agent.port, agent.agent, prepared.prompt,
                prepared.needsRepository ? agent.workspace : undefined,
                { signal: controller.signal, onText: line => { if (line.trim()) setLastLine(line.trim().slice(0, 160)); } },
            );

            setPhase('checking');
            const cli = agent.readOnlyAgents.find(a => a.code === agent.agent)?.displayName ?? agent.agent;
            return await apiService.adminCompleteToolContentGeneration<AgentResult>(toolId, request.kind, request.language, {
                mode: request.mode, output, agent: cli,
            }).catch(error => { throw new Error(errorText(error)); });
        } catch (error) {
            if (controller.signal.aborted) throw new Error('Annullato');
            throw error instanceof Error ? error : new Error(errorText(error));
        } finally {
            abort.current = null;
            setPhase('idle');
            setStartedAt(null);
        }
    }, [agent, toolId]);

    const cancel = useCallback(() => abort.current?.abort(), []);

    return { run, cancel, phase, startedAt, lastLine, busy: phase !== 'idle' };
}

/** Secondi trascorsi da `startedAt`, aggiornati ogni secondo. */
export function useElapsed(startedAt: number | null): number {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        if (!startedAt) return;
        const timer = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(timer);
    }, [startedAt]);
    return startedAt ? Math.max(0, Math.round((now - startedAt) / 1000)) : 0;
}
